import { deserializeJSON, serializeJSON } from "utils/json";
import {
  getLocalStorageValue,
  getSessionStorageValue,
  setLocalStorageValue,
  setSessionStorageValue,
} from "utils/storage";
import { isNullOrUndefOrEmpty, toObject } from "@civicscience/chops";

import { CSSelectOption } from "types";
import { DateLike } from "utils";
import { formatDistance } from "date-fns";

/**
 * NOTE: ActivityTracker uses a combination of localStorage (for full history tracking), and
 * sessionStorage (for tracking a particular area during a user's session). The sessionStorage tracker is primarily
 * used for breadcrumbs, while the localStorage full history tracker is used for a way to get users back to the pages
 * they had used last from the homepage.
 */

const csActivityTrackPrefix = "cs-activity-";
const csActivityTrackMax = 10;
const csActivityTrackID = `${csActivityTrackPrefix}track`;
const csActivityHistoryID = `${csActivityTrackPrefix}history`;
const csActivityHistoryMax = 20;

export type ActivityTrackerName =
  | "dashboard"
  | "question-detail"
  | "question-list"
  | "report-detail"
  | "report-list"
  | "crosstab-detail"
  | "crosstab-list"
  | "target-detail"
  | "target-list"
  | "question-feed-detail"
  | "question-feed-list"
  | "segment-detail"
  | "segment-list"
  | "checkbox-group-detail"
  | "checkbox-group-list"
  | "tag-detail"
  | "tag-list"
  | "checkbox-question-detail"; //NOTE: add more when necessary

export type ActivityTrackerHistoryValue = {
  name: ActivityTrackerName;
  url: string;
  pageTitle: string;
  dateViewed?: DateLike;
};

export type ActivityTrackerAddToHistoryProps = ActivityTrackerHistoryValue;

export type ActivityTrackerTrackProps = ActivityTrackerHistoryValue;

/**
 *
 * addToHistory allows us to save a historical list of urls for all tracked names
 */
const addToHistory = ({ name, url, pageTitle, dateViewed }: ActivityTrackerAddToHistoryProps) => {
  const history = getHistoryStorageDeserialized().slice(0, csActivityHistoryMax - 1);

  //Remove previously saved item that is the same
  const cleanHistory = history.filter((x) => {
    return x.pageTitle !== pageTitle && x.url !== url;
  });
  const updatedHistory = [{ name, url, pageTitle, dateViewed }, ...cleanHistory];

  setLocalStorageValue(`${csActivityHistoryID}`, serializeJSON(updatedHistory, `${csActivityHistoryID}-addToHistory`));
};

/**
 *
 * getHistoryStorageDeserialized deserializes saved string in localStorage to an
 * array of ActivityTrackerHistoryValue
 */
const getHistoryStorageDeserialized = (): ActivityTrackerHistoryValue[] => {
  const historyStr = getLocalStorageValue(`${csActivityHistoryID}`);

  return historyStr ? deserializeJSON(historyStr) : [];
};

/**
 *
 * ActivityTracker.getHistory allows us to access an array of ActivityTrackerHistoryValue regardless of track name
 * from localStorage
 */
const getHistory = (size: number = csActivityHistoryMax): ActivityTrackerHistoryValue[] => {
  const history = getHistoryStorageDeserialized();
  return history.slice(0, size);
};

/**
 *
 * getLastTrackedFromHistory allows us grab the filtered ActivityTrackerHistoryValue of a particular track
 */
const getLastTrackedFromHistory = (name: ActivityTrackerName): ActivityTrackerHistoryValue[] => {
  const history = getHistoryStorageDeserialized();

  return history.filter((historyItem) => historyItem.name === name);
};

/**
 *
 * ActivityTracker.getTracked allows us to access the saved array of historical urls related to a specific track
 * name from sessionStorage
 */
const getTracked = (name: ActivityTrackerName): string[] => {
  const trackedValueStr = getSessionStorageValue(`${csActivityTrackPrefix}${name}`);
  return trackedValueStr ? deserializeJSON(trackedValueStr) : [];
};

/**
 *
 * ActivityTracker.getLastTracked allows us to access the last url saved for a particular track in sessionStorage
 */
const getLastTracked = (name: ActivityTrackerName, fallbackUrl: string): string | undefined =>
  getTracked(name)[0] || fallbackUrl;

/**
 *
 * ActivityTracker.track allows us to save a historical list of urls for a specific tracked name to sessionStorage
 */
const track = ({ name, url, pageTitle, dateViewed }: ActivityTrackerTrackProps) => {
  addToHistory({
    name,
    url,
    pageTitle,
    dateViewed,
  });
  const tracked = getTracked(name).slice(0, csActivityTrackMax - 1); //NOTE: Any tracked items beyond the csActivityTrackMax is removed

  //Remove previously saved item that is the same
  const cleanTrack = tracked.filter((x) => x !== url);
  const updatedTrack = [url, ...cleanTrack];

  setSessionStorageValue(`${csActivityTrackPrefix}${name}`, serializeJSON(updatedTrack, `${csActivityTrackID}`));
};

/**
 *
 * ActivityTracker.sortTracksDescByDate returns a list of ActivityTrackerHistoryValue sorted by date descending
 */
export const sortTracksDescByDate = (allTrackHistory: ActivityTrackerHistoryValue[]) => {
  return allTrackHistory.sort((a, b) => {
    const dateA = a.dateViewed;
    const dateB = b.dateViewed;

    if (!dateA || !dateB) {
      return 0;
    }

    if (dateA < dateB) {
      return 1;
    }
    if (dateA > dateB) {
      return -1;
    }

    // names must be equal
    return 0;
  });
};

/**
 *
 * ActivityTracker.getWhereYouLeftOffOptions returns a list of CSSelectOptions based on activity tracking
 */
const getWhereYouLeftOffOptions = (
  tracks: ActivityTrackerName[] = [
    "dashboard",
    "question-detail",
    "question-list",
    "report-detail",
    "report-list",
    "crosstab-detail",
    "crosstab-list",
    "segment-detail",
    "target-list",
    "target-detail",
    "checkbox-question-detail",
    "checkbox-group-detail",
    "checkbox-group-list",
  ],
): CSSelectOption[] => {
  const allTrackHistory: ActivityTrackerHistoryValue[] = [];

  tracks.flatMap((x) => {
    const tracked = getLastTrackedFromHistory(x);
    tracked && allTrackHistory.push(...tracked);
  });

  const sortedOptions = sortTracksDescByDate(allTrackHistory).map((trackItem): CSSelectOption => {
    const updateTextArray: string[] = [];
    const { pageTitle, url, dateViewed = "" } = toObject(trackItem);
    const hasDate = !isNullOrUndefOrEmpty(dateViewed);

    const parsedDate = hasDate ? new Date(dateViewed) : null;
    const dateRelativeText = parsedDate ? formatDistance(parsedDate, new Date(), { addSuffix: true }) : null;
    !isNullOrUndefOrEmpty(pageTitle) && updateTextArray.push(pageTitle);
    !isNullOrUndefOrEmpty(dateRelativeText) && updateTextArray.push(`(${dateRelativeText})`);

    return {
      text: updateTextArray.join(" "),
      value: isNullOrUndefOrEmpty(url) ? "" : url,
    };
  });

  return sortedOptions.slice(0, csActivityTrackMax);
};

export default {
  activityTrackMax: csActivityTrackMax,
  getHistory,
  getLastTrackedFromHistory,
  getWhereYouLeftOffOptions,
  getTracked,
  getLastTracked,
  track,
};
